// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { listenToStats, listenToLiveDrivers, listenToOnlineRiders, listenToRides, listenToGroceryStats, adminNotifyRiders } from '../services/firebase';
import { FEATURES } from '../config/features';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import {
  todayKey, orderOnDay, formatDayLabel, buildHourlyChart,
} from '../utils/dates';
import DateFilterBar from '../components/DateFilterBar';
import { paymentSplit, cancellationSummary } from '../utils/orders';
import { formatOrderRef } from '../utils/orderRef';
import { exportOrdersCsv } from '../utils/exportCsv';

const minsSince = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return 0;
  return (Date.now() - d.getTime()) / 60000;
};

const getStuckAlerts = (order) => {
  const alerts = [];
  const createdMins = minsSince(order.createdAt);
  const updatedMins = minsSince(order.updatedAt || order.createdAt);

  if (order.status === 'pending' && createdMins >= 10) {
    alerts.push({ severity: 'high', text: `Pending ${Math.floor(createdMins)}m — merchant has not accepted` });
  }
  if (order.status === 'confirmed' && !order.deliveryPartnerId && updatedMins >= 20) {
    alerts.push({ severity: 'high', text: `Confirmed ${Math.floor(updatedMins)}m — no rider assigned` });
  }
  if (order.status === 'ready' && !order.deliveryPartnerId && updatedMins >= 10) {
    alerts.push({ severity: 'medium', text: `Legacy ready ${Math.floor(updatedMins)}m — no rider assigned` });
  }
  if (order.status === 'picked_up' && updatedMins >= 40) {
    alerts.push({ severity: 'medium', text: `Out for delivery ${Math.floor(updatedMins)}m — not marked delivered` });
  }
  return alerts;
};

const orderStatusColor = (s) => {
  if (s === 'delivered') return 'badge-green';
  if (s === 'pending') return 'badge-orange';
  if (s === 'cancelled') return 'badge-red';
  if (['confirmed', 'ready', 'picked_up'].includes(s)) return 'badge-blue';
  return 'badge-gray';
};

const rideStatusColor = (s) => {
  if (s === 'completed') return 'badge-green';
  if (s === 'searching') return 'badge-orange';
  if (s === 'cancelled') return 'badge-red';
  if (['driver_assigned', 'arrived', 'started'].includes(s)) return 'badge-blue';
  return 'badge-gray';
};

const fmt = (d) => {
  if (!d?.toDate) return '—';
  return d.toDate().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

export default function Dashboard() {
  const [stats, setStats] = useState({ rides: [], activeRides: [], completedRides: [], totalRevenue: 0 });
  const [grocery, setGrocery] = useState({ orders: [], activeOrders: [], deliveredOrders: [], groceryRevenue: 0 });
  const [liveDrivers, setLiveDrivers] = useState([]);
  const [recentRides, setRecentRides] = useState([]);
  const [selectedDay, setSelectedDay] = useState(todayKey);
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    const unsubs = FEATURES.RIDES_ENABLED
      ? [listenToLiveDrivers(setLiveDrivers)]
      : [listenToOnlineRiders((riders) => {
          setLiveDrivers(riders.filter((r) => !r.workType || r.workType === 'delivery' || r.workType === 'both'));
        })];
    if (FEATURES.RIDES_ENABLED) {
      unsubs.push(listenToStats(setStats));
      unsubs.push(listenToRides('all', (r) => setRecentRides(r.slice(0, 8))));
    } else {
      unsubs.push(listenToGroceryStats(setGrocery));
    }
    return () => unsubs.forEach((u) => u());
  }, []);

  const dayOrders = useMemo(() => {
    if (FEATURES.RIDES_ENABLED) return [];
    return grocery.orders.filter((o) => orderOnDay(o, selectedDay));
  }, [grocery.orders, selectedDay]);

  const dayDelivered = useMemo(
    () => dayOrders.filter((o) => o.status === 'delivered'),
    [dayOrders],
  );

  const dayRevenue = useMemo(
    () => dayDelivered.reduce((s, o) => s + (o.total || 0), 0),
    [dayDelivered],
  );

  const dayCancelled = useMemo(
    () => dayOrders.filter((o) => o.status === 'cancelled').length,
    [dayOrders],
  );

  const chartSource = FEATURES.RIDES_ENABLED
    ? stats.completedRides.filter((r) => orderOnDay(r, selectedDay))
    : dayDelivered;

  const chartData = FEATURES.RIDES_ENABLED
    ? buildHourlyChart(chartSource, selectedDay, 'fare')
    : buildHourlyChart(chartSource, selectedDay, 'total');

  const dayLabel = formatDayLabel(selectedDay);
  const isToday = selectedDay === todayKey();

  const stuckOrders = useMemo(() => {
    if (FEATURES.RIDES_ENABLED) return [];
    return grocery.activeOrders
      .map((order) => ({ order, alerts: getStuckAlerts(order) }))
      .filter((entry) => entry.alerts.length > 0)
      .sort((a, b) => {
        const aHigh = a.alerts.some((x) => x.severity === 'high');
        const bHigh = b.alerts.some((x) => x.severity === 'high');
        if (aHigh !== bHigh) return aHigh ? -1 : 1;
        return minsSince(b.order.createdAt) - minsSince(a.order.createdAt);
      });
  }, [grocery.activeOrders]);

  const paySplit = useMemo(() => paymentSplit(dayOrders), [dayOrders]);
  const cancelReport = useMemo(() => cancellationSummary(dayOrders), [dayOrders]);

  const handleExport = () => {
    exportOrdersCsv(dayOrders, `mytownly-dashboard-${selectedDay}.csv`);
    toast.success(`Exported ${dayOrders.length} orders`);
  };

  const handleNotifyRiders = async () => {
    setNotifying(true);
    try {
      const result = await adminNotifyRiders({
        body: `${stuckOrders.length} stuck order(s) need attention — open the rider app.`,
      });
      toast.success(`Notified ${result.sent}/${result.total} riders`);
    } catch (e) {
      toast.error(e.message || 'Notify failed');
    } finally {
      setNotifying(false);
    }
  };

  const statCards = FEATURES.RIDES_ENABLED ? [
    { label: 'ACTIVE RIDES', value: stats.activeRides.length, color: 'green', sub: 'Right now' },
    { label: 'RIDE REVENUE', value: `₹${stats.totalRevenue}`, color: 'orange', sub: `${stats.completedRides.length} trips` },
    { label: 'LIVE DRIVERS', value: liveDrivers.length, color: 'blue', sub: 'Online now' },
    { label: 'TOTAL RIDES', value: stats.rides.length, color: 'red', sub: 'All time' },
  ] : [
    { label: 'ACTIVE ORDERS', value: isToday ? grocery.activeOrders.length : '—', color: 'green', sub: isToday ? 'In progress now' : 'Live count · today only' },
    { label: 'DAY REVENUE', value: `₹${dayRevenue}`, color: 'orange', sub: `${dayDelivered.length} delivered · ${dayLabel}` },
    { label: 'DELIVERY ONLINE', value: isToday ? liveDrivers.length : '—', color: 'blue', sub: isToday ? 'Partners online' : 'Live count · today only' },
    { label: 'ORDERS', value: dayOrders.length, color: 'red', sub: `${dayCancelled} cancelled · ${dayLabel}` },
  ];

  return (
    <div className="page-body">
      <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Command Center</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="live-dot" /> Live · Piler, Andhra Pradesh
            {!FEATURES.RIDES_ENABLED && (
              <span className="badge badge-green" style={{ marginLeft: 8 }}>Grocery launch</span>
            )}
          </div>
        </div>
        {!FEATURES.RIDES_ENABLED && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <DateFilterBar selectedDay={selectedDay} onChange={setSelectedDay} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleExport}>Export CSV</button>
          </div>
        )}
      </div>

      {!FEATURES.RIDES_ENABLED && (
        <div className="table-card" style={{ marginBottom: 20, border: stuckOrders.length ? '1px solid rgba(255,152,0,0.35)' : undefined }}>
          <div className="table-header">
            <div className="table-title">⚠️ Needs attention</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {stuckOrders.length > 0 && (
                <button type="button" className="btn btn-orange btn-sm" onClick={handleNotifyRiders} disabled={notifying}>
                  {notifying ? 'Sending…' : 'Notify riders'}
                </button>
              )}
              <span className={`badge ${stuckOrders.length ? 'badge-orange' : 'badge-green'}`}>
                {stuckOrders.length ? `${stuckOrders.length} stuck` : 'All clear'}
              </span>
            </div>
          </div>
          {stuckOrders.length === 0 ? (
            <div style={{ padding: '20px 16px', color: 'var(--text2)', fontSize: 13 }}>
              No delayed orders right now. Alerts appear when pending 10m+, confirmed 20m+, ready without rider 10m+, or out for delivery 40m+.
            </div>
          ) : (
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {stuckOrders.map(({ order, alerts }) => (
                <div key={order.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 12, fontWeight: 700 }}>{formatOrderRef(order)}</div>
                    <span className={`badge ${orderStatusColor(order.status)}`}>{order.status}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{order.shopName || 'Shop'} · ₹{order.total}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
                    {order.customerName || order.customerPhone || order.customerId?.slice(0, 8) || 'Customer'}
                  </div>
                  {alerts.map((alert) => (
                    <div
                      key={alert.text}
                      style={{
                        fontSize: 12,
                        padding: '6px 10px',
                        borderRadius: 8,
                        marginTop: 4,
                        background: alert.severity === 'high' ? 'rgba(255,82,82,0.12)' : 'rgba(255,152,0,0.1)',
                        color: alert.severity === 'high' ? '#ff8a80' : '#ffb74d',
                      }}
                    >
                      {alert.text}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="stat-grid">
        {statCards.map((s) => (
          <div key={s.label} className={`stat-card ${s.color}`}>
            <div className="stat-label">{s.label}</div>
            <div className={`stat-value ${s.color}`}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {!FEATURES.RIDES_ENABLED && (
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 20 }}>
          <div className="stat-card gray">
            <div className="stat-label">COD ORDERS</div>
            <div className="stat-value" style={{ color: 'var(--text)' }}>{paySplit.cod.count}</div>
            <div className="stat-sub">₹{paySplit.cod.total} · {dayLabel}</div>
          </div>
          <div className="stat-card blue">
            <div className="stat-label">ONLINE ORDERS</div>
            <div className="stat-value blue">{paySplit.online.count}</div>
            <div className="stat-sub">₹{paySplit.online.total} · {dayLabel}</div>
          </div>
        </div>
      )}

      {!FEATURES.RIDES_ENABLED && cancelReport.total > 0 && (
        <div className="table-card" style={{ marginBottom: 20 }}>
          <div className="table-header">
            <div className="table-title">Cancellation reasons — {dayLabel}</div>
            <span className="badge badge-red">{cancelReport.total} cancelled</span>
          </div>
          <table>
            <thead><tr><th>REASON</th><th>COUNT</th></tr></thead>
            <tbody>
              {cancelReport.byReason.map((r) => (
                <tr key={r.reason}>
                  <td style={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}>{r.reason}</td>
                  <td>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, marginBottom: 20 }}>
        <div className="table-card" style={{ padding: 20 }}>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            {FEATURES.RIDES_ENABLED ? 'Ride Revenue' : 'Grocery Revenue'} — {dayLabel}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>Hourly breakdown · ₹{dayRevenue} total</div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00e676" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#00e676" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fill: '#4a5568', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#4a5568', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#141a22', border: '1px solid #1e2730', borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#00e676" strokeWidth={2} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="table-card">
          <div className="table-header">
            <div className="table-title">{FEATURES.RIDES_ENABLED ? 'Live Drivers' : 'Online Delivery Partners'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--green)' }}>
              <span className="live-dot" style={{ width: 6, height: 6 }} /> {liveDrivers.length} online
            </div>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {liveDrivers.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>No partners online</div>
            ) : liveDrivers.map((d) => (
              <div key={d.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.name || d.phone || 'Partner'}
                  </div>
                  {d.phone && d.name && (
                    <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{d.phone}</div>
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{d.id.slice(0, 8)}…</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <div className="table-title">{FEATURES.RIDES_ENABLED ? 'Recent Rides' : `Orders — ${dayLabel}`}</div>
          <span className="badge badge-gray">
            {FEATURES.RIDES_ENABLED ? stats.rides.length : dayOrders.length} on this day
          </span>
        </div>
        <table>
          <thead>
            {FEATURES.RIDES_ENABLED ? (
              <tr>
                <th>RIDE ID</th><th>DESTINATION</th><th>DRIVER</th><th>FARE</th><th>PAYMENT</th><th>TIME</th><th>STATUS</th>
              </tr>
            ) : (
              <tr>
                <th>ORDER</th><th>SHOP</th><th>CUSTOMER</th><th>TOTAL</th><th>PAYMENT</th><th>TIME</th><th>STATUS</th>
              </tr>
            )}
          </thead>
          <tbody>
            {FEATURES.RIDES_ENABLED ? (
              recentRides.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)', padding: 24 }}>No rides yet</td></tr>
              ) : recentRides.map((r) => (
                <tr key={r.id}>
                  <td><span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--text2)' }}>#{r.id.slice(0, 8)}</span></td>
                  <td style={{ maxWidth: 160 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.destination?.name || '—'}</div></td>
                  <td>{r.driverName || <span style={{ color: 'var(--text2)' }}>Unassigned</span>}</td>
                  <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)' }}>₹{r.fare || '—'}</td>
                  <td><span className={`badge ${r.paymentMode === 'cash' ? 'badge-gray' : 'badge-blue'}`}>{r.paymentMode || 'cash'}</span></td>
                  <td style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--text2)' }}>{fmt(r.createdAt)}</td>
                  <td><span className={`badge ${rideStatusColor(r.status)}`}>{r.status}</span></td>
                </tr>
              ))
            ) : (
              grocery.orders.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)', padding: 24 }}>No orders yet</td></tr>
              ) : dayOrders.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)', padding: 24 }}>No orders on {dayLabel}</td></tr>
              ) : dayOrders.slice(0, 20).map((o) => (
                <tr key={o.id}>
                  <td><span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--text2)' }}>#{o.id.slice(0, 8)}</span></td>
                  <td>{o.shopName || '—'}</td>
                  <td>{o.customerName || o.customerPhone || '—'}</td>
                  <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)' }}>₹{o.total}</td>
                  <td><span className={`badge ${o.paymentMode === 'cash' ? 'badge-gray' : 'badge-blue'}`}>{o.paymentMode || 'cash'}</span></td>
                  <td style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--text2)' }}>{fmt(o.createdAt)}</td>
                  <td><span className={`badge ${orderStatusColor(o.status)}`}>{o.status}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
